<script lang="ts" setup>
  import { computed, DeepReadonly } from 'vue';

  import useModals, { type Modal } from '../../composables/useModals/useModals';

  const modals = useModals();

  const currentModal = computed(() => modals.current);

  // Stupid TS version issues. Todo: remove ` | any` when TS/vite/vue deps are updated.

  function getListeners(modal: DeepReadonly<Modal> | any) {
    if (modal.options?.disableDefaultListeners) {
      return {};
    }

    return {
      dismiss: () => modals.close({ index: 0 }),
      close: () => modals.close({ index: 0 }),
      cancel: () => modals.close({ index: 0 }),
    };
  }
</script>

<template>
  <div class="stash-modals">
    <Transition name="modals" mode="out-in">
      <component
        :is="currentModal.component"
        v-if="currentModal"
        v-bind="currentModal.attributes"
        :is-open="true"
        v-on="getListeners(currentModal)"
      >
        <template v-for="(value, name) in currentModal.slots" :key="name" #[name]>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="value" />
        </template>
      </component>
    </Transition>
  </div>
</template>

<style module>
  @layer utilities {
    /* No layout box; keeps transition hook element in tree for scoped selectors */
    :global(.stash-modals) {
      display: contents;
    }

    :global(:where(.stash-modals) .modals-enter-active),
    :global(:where(.stash-modals) .modals-leave-active) {
      --swing-timing-function: cubic-bezier(0.25, 0.8, 0.5, 1); /* "swing" from the $transition sass map */
      transition: opacity 165ms var(--swing-timing-function);
    }

    :global(:where(.stash-modals) .modals-enter-from),
    :global(:where(.stash-modals) .modals-leave-to) {
      opacity: 0;
    }

    :global(:where(.stash-modals) .modals-enter-active .stash-modal__dialog--position-center),
    :global(:where(.stash-modals) .modals-leave-active .stash-modal__dialog--position-center) {
      transition: transform 165ms var(--swing-timing-function);
    }

    :global(:where(.stash-modals) .modals-enter-from .stash-modal__dialog--position-center),
    :global(:where(.stash-modals) .modals-leave-to .stash-modal__dialog--position-center) {
      transform: translateY(20px);
    }

    :global(:where(.stash-modals) .modals-enter-active .stash-modal__dialog--is-drawer),
    :global(:where(.stash-modals) .modals-leave-active .stash-modal__dialog--is-drawer) {
      transition: transform 165ms var(--swing-timing-function);
    }

    :global(:where(.stash-modals) .modals-enter-from .stash-modal__dialog--position-left),
    :global(:where(.stash-modals) .modals-leave-to .stash-modal__dialog--position-left) {
      transform: translateX(-50px);
    }

    :global(:where(.stash-modals) .modals-enter-from .stash-modal__dialog--position-right),
    :global(:where(.stash-modals) .modals-leave-to .stash-modal__dialog--position-right) {
      transform: translateX(50px);
    }
  }
</style>
